import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation, useLocale } from "./contexts/LocaleContext";
import { authenticatedFetch } from "./config/api";
import { sanitizeInput } from "./utils/sanitize";
import {
  Bell,
  Search,
  Mail,
  Eye,
  User,
  Clock,
  Check,
  Send,
  AlertCircle,
  Info,
  ChevronRight,
  Trash2,
  Power,
  PowerOff,
  Settings,
  AlertTriangle,
  ExternalLink,
  ArrowUp,
  HelpCircle,
} from "lucide-react";
import ClientDialog from "./ClientDialog";
import EmailConfirmationModal from "./components/EmailConfirmationModal";
import Toggle from "./Toggle";
import SaveButton from "./components/SaveButton";
import {
  getTemplateByStatus,
  getStandardTemplate,
} from "./utils/emailTemplates";
import { Client, InvoiceListItem } from "./types";
import { languages } from "./constants";

interface AutoReminder {
  id: string;
  clientId: string;
  clientName: string;
  clientEmail: string;
  daysAfterDue: number;
  templateType: "standard" | "custom";
  templateLang?: string;
  isActive: boolean;
  lastSentAt?: string;
}

export default function ClientNotifications() {
  const t = useTranslation();
  const { locale } = useLocale();

  // Data state
  const [allInvoices, setAllInvoices] = useState<InvoiceListItem[]>([]);
  const [overdueInvoices, setOverdueInvoices] = useState<InvoiceListItem[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [autoReminders, setAutoReminders] = useState<AutoReminder[]>([]);
  const [isLoadingInvoices, setIsLoadingInvoices] = useState(true);
  const [isLoadingClients, setIsLoadingClients] = useState(true);
  const [isLoadingAuto, setIsLoadingAuto] = useState(true);
  const [appSettings, setAppSettings] = useState({
    notificationEmailHandle: "",
    notificationEmailName: "",
  });
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  const [hasSavedSettings, setHasSavedSettings] = useState(true); // Default to true to avoid flicker on load
  const [settingsError, setSettingsError] = useState<string | null>(null);

  // Search state
  const [invoiceSearchQuery, setInvoiceSearchQuery] = useState("");
  const [clientSearchQuery, setSearchQuery] = useState("");

  // Modal state
  const [isClientDialogOpen, setIsClientDialogOpen] = useState(false);

  // Configuration state
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
  const [selectedPreviewInvoiceId, setSelectedPreviewInvoiceId] = useState<
    string | null
  >(null);
  const [reminderDays, setReminderDays] = useState<string>("1");
  const [templateType, setTemplateType] = useState<"standard" | "custom">(
    "standard"
  );
  const [templateLang, setTemplateLang] = useState<string>(locale);
  const [customSubject, setCustomSubject] = useState("");
  const [customText, setCustomText] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isSendingManual, setIsSendingManual] = useState<string | null>(null);
  const [manualView, setManualView] = useState<"overdue" | "all">("all");
  const [confirmationType, setConfirmationType] = useState<"manual" | "auto">(
    "manual"
  );

  // Confirmation Modal State
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmInvoice, setConfirmInvoice] = useState<InvoiceListItem | null>(
    null
  );
  const [confirmSubject, setConfirmSubject] = useState("");
  const [confirmText, setConfirmText] = useState("");

  // Fetch data
  const loadData = async () => {
    try {
      setIsLoadingClients(true);
      setIsLoadingAuto(true);

      // 1. Fetch settings and clients FIRST (critical for UI)
      const [settingsRes, clientsResponse] = await Promise.all([
        authenticatedFetch("/api/settings"),
        authenticatedFetch("/api/clients"),
      ]);

      if (settingsRes.ok) {
        const data = await settingsRes.json();
        const hasSettings = !!(
          data.notificationEmailHandle && data.notificationEmailName
        );
        setAppSettings({
          notificationEmailHandle: data.notificationEmailHandle || "",
          notificationEmailName: data.notificationEmailName || "",
        });
        setHasSavedSettings(hasSettings);
      }

      if (clientsResponse.ok) {
        setClients(await clientsResponse.json());
      }
      setIsLoadingClients(false);

      // 2. Load auto reminders from localStorage
      const storedReminders = localStorage.getItem("auto-reminders");
      if (storedReminders) {
        setAutoReminders(JSON.parse(storedReminders));
      }
      setIsLoadingAuto(false);

      // 3. Fetch invoices (full data in one request to avoid rate limits)
      setIsLoadingInvoices(true);
      const invResponse = await authenticatedFetch("/api/invoices-full");
      if (invResponse.ok) {
        const fullInvoices = await invResponse.json();
        const convertedInvoices: InvoiceListItem[] = fullInvoices.map(
          (inv: any) => {
            const activeVersion = inv; // The new endpoint returns the active version directly
            if (activeVersion && activeVersion.draft) {
              const computedAmount = Array.isArray(activeVersion.draft.items)
                ? activeVersion.draft.items.reduce(
                    (sum: number, item: any) =>
                      sum + (item?.amount || 0) * (item?.quantity || 1),
                    0
                  )
                : 0;

              const today = new Date().toISOString().split("T")[0];
              const isOverdue =
                activeVersion.draft.status !== "paid" &&
                activeVersion.draft.dueDate < today;

              // Extract payment details
              let paymentInfo = "";
              const draft = activeVersion.draft;

              const methodParts: string[] = [];

              // 1. Process paymentOptions (modern way)
              if (
                draft.paymentOptions &&
                Array.isArray(draft.paymentOptions) &&
                draft.paymentOptions.length > 0
              ) {
                draft.paymentOptions.forEach((opt: any) => {
                  let details = opt.title ? `${opt.title}: ` : "";
                  if (opt.type === "bank") {
                    const bankParts = [];
                    const bData = opt.bankTransfer || opt;

                    if (bData.bankName) bankParts.push(bData.bankName);
                    if (bData.accountNumber)
                      bankParts.push(bData.accountNumber);
                    if (bData.iban) bankParts.push(`IBAN: ${bData.iban}`);
                    if (bData.bic) bankParts.push(`BIC: ${bData.bic}`);

                    const vs =
                      bData.variableSymbol ||
                      opt.variableSymbol ||
                      draft.variableSymbol;
                    if (vs) bankParts.push(`VS: ${vs}`);

                    if (bankParts.length > 0) {
                      details += bankParts.join(", ");
                    } else if (opt.details) {
                      details += opt.details;
                    }
                  } else if (opt.type === "link" && opt.paymentUrl) {
                    details += opt.paymentUrl;
                  } else if (opt.details) {
                    details += opt.details;
                  }

                  if (details) methodParts.push(details);
                });
              }

              // 2. Fallback or addition of legacy paymentInfo (bank) if not already present
              if (
                draft.paymentInfo &&
                (draft.paymentInfo.accountNumber ||
                  draft.paymentInfo.iban ||
                  draft.paymentInfo.bankTransfer)
              ) {
                const pi = draft.paymentInfo;
                const bData = pi.bankTransfer || pi;

                const isDuplicate = methodParts.some(
                  (part) =>
                    (bData.accountNumber &&
                      part.includes(bData.accountNumber)) ||
                    (bData.iban && part.includes(bData.iban))
                );

                if (!isDuplicate) {
                  const bankParts = [];
                  if (bData.bankName) bankParts.push(bData.bankName);
                  if (bData.accountNumber) bankParts.push(bData.accountNumber);
                  if (bData.iban) bankParts.push(`IBAN: ${bData.iban}`);
                  if (bData.bic) bankParts.push(`BIC: ${bData.bic}`);

                  const vs =
                    bData.variableSymbol ||
                    pi.variableSymbol ||
                    draft.variableSymbol;
                  if (vs) bankParts.push(`VS: ${vs}`);

                  if (bankParts.length > 0) {
                    methodParts.push(`Bank: ${bankParts.join(", ")}`);
                  }
                }
              }

              // 3. Fallback or addition of legacy paymentLink if not already present
              if (draft.paymentLink && draft.paymentLink.url) {
                const url = draft.paymentLink.url;
                const isDuplicate = methodParts.some((part) =>
                  part.includes(url)
                );
                if (!isDuplicate) {
                  methodParts.push(`Link: ${url}`);
                }
              }

              paymentInfo = methodParts.filter(Boolean).join(" -- или -- ");

              return {
                id: inv.internalId,
                internalId: inv.internalId,
                invoiceNo: draft.invoiceNo || "",
                clientName: draft.buyerName || "",
                clientEmail: draft.buyerEmail || "",
                description:
                  (draft.items?.length > 0
                    ? draft.items[0]?.description
                    : "") || "",
                amount: computedAmount,
                currency: draft.currency || "CZK",
                issueDate: draft.issueDate || "",
                dueDate: draft.dueDate || "",
                status: (isOverdue
                  ? "overdue"
                  : draft.status || "pending") as any,
                createdAt: inv.createdAt || new Date().toISOString(),
                paymentDetails: paymentInfo,
                sellerName:
                  draft.sellerInfo?.name ||
                  draft.seller?.name ||
                  draft.sellerName ||
                  "",
                variableSymbol: draft.variableSymbol || draft.invoiceNo || "",
              };
            }
            return null;
          }
        );

        setAllInvoices(
          convertedInvoices.filter((inv) => inv !== null) as InvoiceListItem[]
        );
        setOverdueInvoices(
          convertedInvoices.filter(
            (inv) => inv !== null && inv.status === "overdue"
          ) as InvoiceListItem[]
        );
      }
    } catch (error) {
      console.error("Error loading data:", error);
    } finally {
      setIsLoadingInvoices(false);
      setIsLoadingClients(false);
      setIsLoadingAuto(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleSaveEmailSettings = async () => {
    setIsSavingSettings(true);
    setSettingsError(null);
    try {
      const response = await authenticatedFetch("/api/settings", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          notificationEmailName: appSettings.notificationEmailName,
          notificationEmailHandle: appSettings.notificationEmailHandle,
        }),
      });

      if (response.ok) {
        setHasSavedSettings(true);
        alert(t.invoiceList.settings.saved);
      } else {
        const data = await response.json();
        if (data.error === "FORBIDDEN_HANDLE") {
          setSettingsError(
            t.userSettings.general.notificationEmailHandleErrorForbidden
          );
        } else if (data.error === "HANDLE_TAKEN") {
          setSettingsError(
            t.userSettings.general.notificationEmailHandleErrorTaken
          );
        } else {
          setSettingsError(data.error || "Failed to update settings");
        }
      }
    } catch (error: any) {
      console.error("Error updating settings:", error);
      setSettingsError("An unexpected error occurred");
    } finally {
      setIsSavingSettings(false);
    }
  };

  const handleToggleAutoStatus = (id: string) => {
    const updated = autoReminders.map((r) =>
      r.id === id ? { ...r, isActive: !r.isActive } : r
    );
    setAutoReminders(updated);
    localStorage.setItem("auto-reminders", JSON.stringify(updated));
  };

  const handleDeleteAutoReminder = (id: string) => {
    if (window.confirm(t.clientNotifications.deleteReminderConfirm)) {
      const updated = autoReminders.filter((r) => r.id !== id);
      setAutoReminders(updated);
      localStorage.setItem("auto-reminders", JSON.stringify(updated));
    }
  };

  const filteredInvoices = useMemo(() => {
    const baseInvoices = manualView === "all" ? allInvoices : overdueInvoices;

    if (!invoiceSearchQuery) return baseInvoices;
    const query = invoiceSearchQuery.toLowerCase();
    return baseInvoices.filter(
      (inv) =>
        inv.invoiceNo.toLowerCase().includes(query) ||
        inv.clientName.toLowerCase().includes(query) ||
        inv.description.toLowerCase().includes(query)
    );
  }, [allInvoices, overdueInvoices, invoiceSearchQuery, manualView]);

  const previewInvoices = useMemo(() => {
    // Only real invoices for preview selection
    return allInvoices;
  }, [allInvoices]);

  const filteredClients = useMemo(() => {
    if (!clientSearchQuery) return clients;
    const query = clientSearchQuery.toLowerCase();
    return clients.filter(
      (c) =>
        c.name.toLowerCase().includes(query) ||
        (c.email || "").toLowerCase().includes(query)
    );
  }, [clients, clientSearchQuery]);
  const formatDate = (dateStr: string) => {
    if (!dateStr) return "";
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return dateStr;
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    } catch (e) {
      return dateStr;
    }
  };

  const getInvoiceVariables = (invoice: InvoiceListItem | undefined) => {
    if (!invoice)
      return {
        invoiceNo: "{invoiceNo}",
        issueDate: "{issueDate}",
        amount: "{amount}",
        paymentReference: "{paymentReference}",
        paymentDetails: "{paymentDetails}",
        companyName: "{companyName}",
      };

    return {
      invoiceNo: invoice.invoiceNo || "{invoiceNo}",
      issueDate: formatDate(invoice.issueDate) || "{issueDate}",
      amount:
        invoice.amount !== undefined
          ? `${invoice.amount.toLocaleString("cs-CZ", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })} ${invoice.currency === "CZK" ? "Kč" : invoice.currency}`
          : "{amount}",
      paymentReference: invoice.variableSymbol || "{paymentReference}",
      paymentDetails: invoice.paymentDetails || "{paymentDetails}",
      companyName: invoice.sellerName || "{companyName}",
    };
  };

  const fillTemplateWithData = (
    template: string,
    invoice: InvoiceListItem | undefined
  ) => {
    const vars = getInvoiceVariables(invoice);

    return (template || "")
      .replace(/{invoiceNo}/g, vars.invoiceNo)
      .replace(/{issueDate}/g, vars.issueDate)
      .replace(/{amount}/g, vars.amount)
      .replace(/{paymentReference}/g, vars.paymentReference)
      .replace(/{paymentDetails}/g, vars.paymentDetails)
      .replace(/{companyName}/g, vars.companyName);
  };

  const standardTemplate = getStandardTemplate(templateLang);
  const basePreviewText =
    templateType === "standard" ? standardTemplate.body : customText;
  const basePreviewSubject =
    templateType === "standard" ? standardTemplate.subject : customSubject;

  const selectedPreviewInvoice = previewInvoices.find(
    (inv) => inv.id === selectedPreviewInvoiceId
  );

  const previewText = fillTemplateWithData(
    basePreviewText,
    selectedPreviewInvoice
  );
  const previewSubject = fillTemplateWithData(
    basePreviewSubject,
    selectedPreviewInvoice
  );

  const selectedClient = clients.find((c) => c.id === selectedClientId);
  const isEmailMissing = !!(
    selectedClientId &&
    selectedClient &&
    !selectedClient.email
  );

  const handleSendManual = async (invoiceId: string) => {
    const invoice = allInvoices.find((inv) => inv.id === invoiceId);
    if (!invoice) return;

    // Calculate subject and body for THIS invoice
    const template = getTemplateByStatus(invoice.status, templateLang);
    const baseSubject = template.subject;
    const baseText = template.body;

    const subject = fillTemplateWithData(baseSubject, invoice);
    const text = fillTemplateWithData(baseText, invoice);

    // Open confirmation modal
    setConfirmationType("manual");
    setConfirmInvoice(invoice);
    setConfirmSubject(subject);
    setConfirmText(text);
    setShowConfirmModal(true);
  };

  const handleOpenAutoPreview = () => {
    if (!selectedClientId) return;

    const client = clients.find((c) => c.id === selectedClientId);
    if (!client) return;

    // Use selected preview invoice or first available or dummy
    const invoice =
      selectedPreviewInvoice ||
      allInvoices[0] ||
      ({
        invoiceNo: "2026001",
        issueDate: "2026-01-05",
        amount: 10.0,
        currency: "CZK",
        sellerName: t.clientNotifications.placeholderCompany,
        paymentDetails: t.clientNotifications.placeholderPayment,
        clientEmail: client.email || "",
      } as any);

    const template = getTemplateByStatus(
      invoice.status || "overdue",
      templateLang
    );
    const subject = fillTemplateWithData(template.subject, invoice);
    const text = fillTemplateWithData(template.body, invoice);

    setConfirmationType("auto");
    setConfirmInvoice(invoice);
    setConfirmSubject(subject);
    setConfirmText(text);
    setShowConfirmModal(true);
  };

  const confirmSendManual = async (
    to: string,
    finalSubject: string,
    finalText: string
  ) => {
    if (!confirmInvoice) return;

    if (confirmationType === "auto") {
      await handleSaveSettings(to);
      setShowConfirmModal(false);
      return;
    }

    setIsSendingManual(confirmInvoice.id);
    setShowConfirmModal(false);

    try {
      const response = await authenticatedFetch("/api/send-reminder", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          to: to || confirmInvoice.clientEmail,
          subject: finalSubject,
          text: finalText,
          invoiceId: confirmInvoice.internalId,
        }),
      });

      if (response.ok) {
        alert(t.clientNotifications.reminderSent);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to send reminder");
      }
    } catch (e: any) {
      console.error(e);
      alert(t.clientNotifications.reminderError);
    } finally {
      setIsSendingManual(null);
      setConfirmInvoice(null);
    }
  };

  const handleSaveSettings = async (emailFromModal?: string) => {
    if (!selectedClientId) return;
    setIsSaving(true);
    try {
      const client = clients.find((c) => c.id === selectedClientId);
      const finalEmail = emailFromModal || client?.email || "";

      const newReminder: AutoReminder = {
        id: Math.random().toString(36).substr(2, 9),
        clientId: selectedClientId,
        clientName: client?.name || "Unknown",
        clientEmail: finalEmail,
        daysAfterDue: parseInt(reminderDays),
        templateType,
        templateLang: templateType === "standard" ? templateLang : undefined,
        isActive: true,
      };

      const updated = [
        ...autoReminders.filter((r) => r.clientId !== selectedClientId),
        newReminder,
      ];
      setAutoReminders(updated);
      localStorage.setItem("auto-reminders", JSON.stringify(updated));

      // If email was provided in modal but missing in client, update client locally/backend
      if (emailFromModal && client && !client.email) {
        await updateClient(client.id, { ...client, email: emailFromModal });
      }

      setSelectedClientId(null);
      setReminderDays("1");
      setTemplateType("standard");
      setCustomText("");
      alert("Settings saved!");
    } catch (e) {
      console.error(e);
    } finally {
      setIsSaving(false);
    }
  };

  const addClient = async (client: Omit<Client, "id">) => {
    try {
      const response = await authenticatedFetch("/api/clients", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(client),
      });
      if (!response.ok) throw new Error("Failed to save client");
      const newClient = await response.json();
      setClients((prev) => [...prev, newClient]);
    } catch (error) {
      console.error("Error saving client:", error);
      alert("Failed to save client");
    }
  };

  const updateClient = async (id: string, client: Omit<Client, "id">) => {
    try {
      const response = await authenticatedFetch(`/api/clients/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(client),
      });
      if (!response.ok) throw new Error("Failed to update client");
      const updatedClient = await response.json();
      setClients((prev) => prev.map((c) => (c.id === id ? updatedClient : c)));
    } catch (error) {
      console.error("Error updating client:", error);
      alert("Failed to update client");
    }
  };

  const deleteClient = async (id: string) => {
    try {
      const response = await authenticatedFetch(`/api/clients/${id}`, {
        method: "DELETE",
      });
      if (!response.ok) throw new Error("Failed to delete client");
      setClients((prev) => prev.filter((c) => c.id !== id));
      if (selectedClientId === id) setSelectedClientId(null);
    } catch (error) {
      console.error("Error deleting client:", error);
      alert("Failed to delete client");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50/30 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800 transition-colors">
      <div className="container mx-auto px-1.5 sm:px-4 py-6 sm:py-8 max-w-7xl">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100 transition-colors">
            {t.clientNotifications.title}
          </h1>

          <div className="mt-4 bg-white dark:bg-slate-800/50 rounded-2xl border border-slate-200 dark:border-slate-700 p-4 sm:p-5 shadow-sm transition-colors overflow-hidden">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-4">
              <h2 className="text-sm font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
                <Settings className="w-4 h-4 text-indigo-500 flex-shrink-0" />
                <span className="leading-tight">
                  {t.clientNotifications.setupNotice}
                </span>
              </h2>
              <SaveButton
                onClick={handleSaveEmailSettings}
                isSaving={isSavingSettings}
                disabled={
                  !appSettings.notificationEmailName.trim() ||
                  !appSettings.notificationEmailHandle.trim()
                }
                text={t.common.save || "Save"}
                className="!text-xs !px-4 !py-2 w-full lg:w-auto"
              />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="block text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest px-1">
                    {t.userSettings.general.notificationEmailName}
                  </label>
                  <input
                    type="text"
                    value={appSettings.notificationEmailName}
                    onChange={(e) =>
                      setAppSettings((prev) => ({
                        ...prev,
                        notificationEmailName: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 text-xs bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-indigo-500/50 transition-all text-slate-900 dark:text-slate-100 outline-none"
                    placeholder="e.g. Alice"
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="block text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest px-1">
                    {t.userSettings.general.notificationEmailHandle}
                  </label>
                  <div className="flex items-center">
                    <input
                      type="text"
                      value={appSettings.notificationEmailHandle}
                      onChange={(e) =>
                        setAppSettings((prev) => ({
                          ...prev,
                          notificationEmailHandle: e.target.value,
                        }))
                      }
                      className={`flex-1 min-w-0 px-3 py-2 text-xs bg-slate-50 dark:bg-slate-900 border ${
                        settingsError && settingsError.includes("@invoitio.com")
                          ? "border-red-500"
                          : "border-slate-200 dark:border-slate-700"
                      } rounded-l-xl focus:ring-2 focus:ring-indigo-500/50 transition-all text-slate-900 dark:text-slate-100 outline-none`}
                      placeholder="e.g. alice"
                    />
                    <span className="px-2 sm:px-3 py-2 text-[9px] sm:text-[10px] bg-slate-100 dark:bg-slate-800 border border-l-0 border-slate-200 dark:border-slate-700 rounded-r-xl text-slate-500 font-bold whitespace-nowrap">
                      @invoitio.com
                    </span>
                  </div>
                </div>

                {settingsError && (
                  <div className="sm:col-span-2 px-1">
                    <p className="text-[10px] font-bold text-red-500 dark:text-red-400">
                      {settingsError}
                    </p>
                  </div>
                )}
              </div>

              <div className="space-y-3">
                <div className="bg-slate-50 dark:bg-slate-900/50 rounded-xl p-3 border border-slate-100 dark:border-slate-800">
                  <p className="text-[9px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-[0.2em] mb-2">
                    {t.userSettings.general.notificationEmailPreview}
                  </p>
                  <div className="flex flex-col gap-0.5">
                    <span className="text-xs font-bold text-slate-900 dark:text-slate-100 break-all">
                      {appSettings.notificationEmailName || "Name"}
                    </span>
                    <span className="text-[10px] sm:text-[11px] text-indigo-600 dark:text-indigo-400 font-medium break-all">
                      {(
                        appSettings.notificationEmailHandle || "handle"
                      ).toLowerCase()}
                      @invoitio.com
                    </span>
                  </div>
                </div>
                <p className="text-[10px] text-slate-500 dark:text-slate-400 font-medium leading-relaxed">
                  {t.clientNotifications.setupAlwaysChange}{" "}
                  <Link
                    to="/settings"
                    className="text-indigo-600 dark:text-indigo-400 font-bold hover:underline"
                  >
                    {t.userSettings.general.title}
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-8 relative">
          {/* 
          {!hasSavedSettings && !isLoadingClients && (
            <div className="absolute -inset-2 z-[40] bg-white/60 dark:bg-slate-900/60 backdrop-blur-[1px] rounded-[2rem] flex items-center justify-center cursor-not-allowed border-2 border-dashed border-slate-200 dark:border-slate-700 pointer-events-auto">
              <div className="bg-white dark:bg-slate-800 p-8 rounded-3xl shadow-2xl border border-slate-200 dark:border-slate-700 max-w-sm text-center space-y-4 mx-4">
                <div className="w-16 h-16 bg-amber-50 dark:bg-amber-900/30 rounded-2xl flex items-center justify-center mx-auto text-amber-600 dark:text-amber-400">
                  <AlertCircle className="w-8 h-8" />
                </div>
                <div className="space-y-2">
                  <h3 className="text-lg font-black text-slate-900 dark:text-slate-100">
                    {t.clientNotifications.setupRequiredTitle}
                  </h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed font-medium">
                    {t.clientNotifications.setupRequiredDescription}
                  </p>
                </div>
              </div>
            </div>
          )}
          */}
          {/* Manual Section */}
          <section
            id="manual-reminder-section"
            className="bg-white dark:bg-slate-800/50 rounded-3xl border border-slate-200 dark:border-slate-700 p-4 sm:p-6 md:p-8 shadow-sm transition-colors overflow-hidden"
          >
            <div className="flex items-center gap-3 mb-6 sm:mb-8">
              <div className="p-2 sm:p-3 bg-blue-100 dark:bg-blue-900/30 rounded-2xl flex-shrink-0">
                <Send className="w-5 h-5 sm:w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
              <h2 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-slate-100">
                {t.clientNotifications.manualSection}
              </h2>
            </div>

            <div className="mb-8 sm:mb-12">
              {/* Step 2: Select real invoice */}
              <div className="space-y-6">
                <div className="bg-slate-50/50 dark:bg-slate-900/30 rounded-3xl p-4 sm:p-6 border border-slate-100 dark:border-slate-800/50 min-h-[400px] flex flex-col">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
                    <div className="grid grid-cols-2 sm:flex bg-slate-100 dark:bg-slate-800 p-1 rounded-xl w-full sm:w-auto">
                      <button
                        onClick={() => setManualView("all")}
                        className={`px-2 sm:px-4 py-2 rounded-lg text-[10px] sm:text-xs font-bold transition-all text-center ${
                          manualView === "all"
                            ? "bg-white dark:bg-slate-700 text-blue-600 dark:text-blue-400 shadow-sm"
                            : "text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
                        }`}
                      >
                        {t.clientNotifications.allInvoices}
                      </button>
                      <button
                        onClick={() => setManualView("overdue")}
                        className={`px-2 sm:px-4 py-2 rounded-lg text-[10px] sm:text-xs font-bold transition-all text-center ${
                          manualView === "overdue"
                            ? "bg-rose-50 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400 shadow-sm"
                            : "text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
                        }`}
                      >
                        {t.clientNotifications.overdueInvoicesOnly}
                      </button>
                    </div>

                    <div className="relative flex-1 sm:max-w-[200px] w-full">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Search className="h-4 w-4 text-slate-400" />
                      </div>
                      <input
                        type="text"
                        placeholder={t.common.search || "Search..."}
                        value={invoiceSearchQuery}
                        onChange={(e) => setInvoiceSearchQuery(e.target.value)}
                        className="w-full pl-9 pr-4 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-xs text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-blue-500/50 outline-none transition-all"
                      />
                    </div>
                  </div>

                  <div className="flex-1 overflow-hidden flex flex-col">
                    <div className="overflow-x-auto max-h-[450px] overflow-y-auto custom-scrollbar -mx-4 px-4 sm:mx-0 sm:px-0">
                      <table className="w-full min-w-[450px]">
                        <thead className="sticky top-0 bg-white dark:bg-slate-900 z-10 shadow-[0_1px_0_0_rgba(0,0,0,0.05)]">
                          <tr className="text-left text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-[0.2em]">
                            <th className="px-4 py-3">{t.common.name}</th>
                            <th className="px-4 py-3 text-right">
                              {t.common.amount}
                            </th>
                            <th className="px-4 py-3 text-center">
                              {t.common.actions}
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-50 dark:divide-slate-800/50">
                          {isLoadingInvoices ? (
                            [...Array(3)].map((_, i) => (
                              <tr key={i} className="animate-pulse">
                                <td
                                  colSpan={3}
                                  className="px-4 py-3 h-12 bg-slate-50/50 dark:bg-slate-900/20 rounded-xl mb-2"
                                />
                              </tr>
                            ))
                          ) : filteredInvoices.length > 0 ? (
                            filteredInvoices.map((inv) => (
                              <tr
                                key={inv.id}
                                onClick={() =>
                                  setSelectedPreviewInvoiceId(
                                    selectedPreviewInvoiceId === inv.id
                                      ? null
                                      : inv.id
                                  )
                                }
                                className={`group cursor-pointer transition-colors ${
                                  selectedPreviewInvoiceId === inv.id
                                    ? "bg-blue-50/50 dark:bg-blue-900/10"
                                    : "hover:bg-white dark:hover:bg-slate-800"
                                }`}
                              >
                                <td className="px-4 py-3">
                                  <div className="flex flex-col">
                                    <span className="font-bold text-slate-900 dark:text-slate-100 text-sm">
                                      #{inv.invoiceNo}
                                    </span>
                                    <span className="text-[10px] text-slate-500 truncate max-w-[150px]">
                                      {inv.clientName}
                                    </span>
                                  </div>
                                </td>
                                <td className="px-4 py-3 text-right">
                                  <span className="font-black text-slate-900 dark:text-white text-xs whitespace-nowrap">
                                    {inv.amount.toLocaleString("cs-CZ", {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                    })}{" "}
                                    {inv.currency}
                                  </span>
                                </td>
                                <td className="px-4 py-3">
                                  <div className="flex justify-center">
                                    <button
                                      onClick={() => handleSendManual(inv.id)}
                                      disabled={isSendingManual === inv.id}
                                      className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-colors disabled:opacity-50 font-bold text-[10px]"
                                    >
                                      {isSendingManual === inv.id ? (
                                        <motion.div
                                          animate={{ rotate: 360 }}
                                          transition={{
                                            repeat: Infinity,
                                            duration: 1,
                                          }}
                                        >
                                          <Clock className="w-3 h-3" />
                                        </motion.div>
                                      ) : (
                                        <Send className="w-3 h-3" />
                                      )}
                                      <span>
                                        {t.clientNotifications.previewAndSend}
                                      </span>
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan={3} className="px-4 py-8 text-center">
                                <p className="text-slate-400 dark:text-slate-500 italic text-xs">
                                  {manualView === "overdue"
                                    ? t.clientNotifications.noOverdue
                                    : t.clientNotifications.noInvoicesFound}
                                </p>
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Auto Section */}
          {/* 
          <section className="relative overflow-hidden bg-white dark:bg-slate-800/50 rounded-3xl border border-slate-200 dark:border-slate-700 p-6 md:p-8 shadow-sm transition-colors">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-2xl">
                <Bell className="w-6 h-6 text-purple-600 dark:text-purple-400" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
                {t.clientNotifications.autoSection}
              </h2>
            </div>

            <div className="">
              <div className="mb-8 p-6 bg-yellow-50/80 dark:bg-yellow-900/10 rounded-2xl border border-yellow-200 dark:border-yellow-900/30">
                <div className="flex items-center gap-2 mb-2 text-yellow-800 dark:text-yellow-400 font-black text-sm">
                  <HelpCircle className="w-5 h-5" />
                  {t.clientNotifications.howItWorksTitle}
                </div>
                <p className="text-xs sm:text-sm text-slate-700 dark:text-slate-400 leading-relaxed font-medium">
                  {t.clientNotifications.howItWorksDescription
                    .split(/({[a-zA-Z]+})/)
                    .map((part: string, index: number) => {
                      if (part === "{clientLink}") {
                        return (
                          <button
                            key={index}
                            onClick={() => setIsClientDialogOpen(true)}
                            className="text-blue-600 dark:text-blue-400 font-bold hover:underline inline-flex items-center gap-0.5 p-0 border-none bg-transparent align-baseline"
                          >
                            <span className="leading-none">
                              {t.clientNotifications.clientLink}
                            </span>
                            <ExternalLink className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                          </button>
                        );
                      }
                      if (part === "{appLink}") {
                        return (
                          <Link
                            key={index}
                            to={`/${locale}/app`}
                            className="text-blue-600 dark:text-blue-400 font-bold hover:underline inline-flex items-center gap-0.5 align-baseline"
                          >
                            <span className="leading-none">
                              {t.clientNotifications.appLink}
                            </span>
                            <ExternalLink className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                          </Link>
                        );
                      }
                      if (part === "{manualLink}") {
                        return (
                          <button
                            key={index}
                            onClick={() => {
                              document
                                .getElementById("manual-reminder-section")
                                ?.scrollIntoView({ behavior: "smooth" });
                            }}
                            className="text-blue-600 dark:text-blue-400 font-bold hover:underline inline-flex items-center gap-0.5 p-0 border-none bg-transparent align-baseline"
                          >
                            <span className="leading-none">
                              {t.clientNotifications.manualLink}
                            </span>
                            <ArrowUp className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                          </button>
                        );
                      }
                      return (
                        <span key={index} className="inline">
                          {part}
                        </span>
                      );
                    })}
                </p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
                <div className="space-y-6">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100 flex items-start gap-2">
                      <span className="w-8 h-8 shrink-0 rounded-full bg-purple-600 text-white flex items-center justify-center text-sm font-black mt-0.5">
                        1
                      </span>
                      {t.clientNotifications.autoStep1}
                    </h2>
                    <button
                      onClick={() => setIsClientDialogOpen(true)}
                      className="flex items-center justify-center gap-2 px-4 py-2 rounded-xl bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-100 dark:hover:bg-indigo-900/40 transition-colors font-bold text-xs border border-indigo-100 dark:border-indigo-800 w-full sm:w-auto"
                    >
                      <Settings className="w-4 h-4" />
                      <span>{t.invoice.manageClients}</span>
                    </button>
                  </div>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <Search className="h-5 w-5 text-slate-400" />
                    </div>
                    <input
                      type="text"
                      placeholder={t.clientNotifications.searchClients}
                      value={clientSearchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-11 pr-4 py-3 rounded-2xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-purple-500/50 transition-all outline-none"
                    />
                  </div>
                  <div className="max-h-[400px] overflow-y-auto space-y-2 pr-2 custom-scrollbar">
                    {isLoadingClients ? (
                      [...Array(3)].map((_, i) => (
                        <div
                          key={i}
                          className="h-16 bg-slate-50 dark:bg-slate-900/50 rounded-2xl animate-pulse"
                        />
                      ))
                    ) : filteredClients.length > 0 ? (
                      filteredClients.map((client) => (
                        <div
                          key={client.id}
                          onClick={() =>
                            setSelectedClientId(
                              selectedClientId === client.id ? null : client.id
                            )
                          }
                          className={`p-4 rounded-2xl border-2 cursor-pointer transition-all ${
                            selectedClientId === client.id
                              ? "border-purple-600 bg-purple-50 dark:bg-purple-900/20"
                              : "border-slate-100 dark:border-slate-800 hover:border-purple-200 dark:hover:border-purple-800"
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <div
                              className={`p-2 rounded-xl ${
                                selectedClientId === client.id
                                  ? "bg-purple-600 text-white"
                                  : "bg-slate-100 dark:bg-slate-800 text-slate-400"
                              }`}
                            >
                              <User className="w-5 h-5" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-bold text-slate-900 dark:text-slate-100 truncate">
                                {client.name}
                              </p>
                              <div className="flex items-center gap-2 text-xs text-slate-500 truncate">
                                {client.email && <span>{client.email}</span>}
                                {client.email && client.phone && <span>•</span>}
                                {client.phone && <span>{client.phone}</span>}
                              </div>
                            </div>
                            {selectedClientId === client.id && (
                              <Check className="w-5 h-5 text-purple-600" />
                            )}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="py-12 text-center bg-slate-50 dark:bg-slate-900/30 rounded-3xl space-y-4">
                        <p className="text-slate-400 dark:text-slate-500 italic">
                          {t.clientNotifications.noClients}
                        </p>
                        <button
                          onClick={() => setIsClientDialogOpen(true)}
                          className="inline-flex items-center gap-2 px-6 py-3 rounded-2xl bg-white dark:bg-slate-800 text-purple-600 dark:text-purple-400 border-2 border-purple-100 dark:border-purple-900/30 hover:border-purple-600 transition-all font-bold text-sm shadow-sm"
                        >
                          <User className="w-4 h-4" />
                          <span>{t.clientDialog.addTitle}</span>
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                <div className="space-y-6">
                  <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100 flex items-start gap-2">
                    <span className="w-8 h-8 shrink-0 rounded-full bg-purple-600 text-white flex items-center justify-center text-sm font-black mt-0.5">
                      2
                    </span>
                    {t.clientNotifications.autoStep2}
                  </h2>
                  <div className="bg-slate-50 dark:bg-slate-900/50 rounded-3xl p-4 sm:p-6 space-y-6 border border-slate-100 dark:border-slate-800/50 flex flex-col">
                    <div className="space-y-6">
                      <div className="space-y-3">
                        <label className="text-sm font-bold text-slate-700 dark:text-slate-300 block">
                          {t.clientNotifications.autoIntervalLabel}
                        </label>
                        <div className="flex items-center gap-3">
                          <input
                            type="number"
                            min="1"
                            value={reminderDays}
                            onChange={(e) => setReminderDays(e.target.value)}
                            className="w-20 px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-purple-500/50 outline-none text-center font-black"
                          />
                          <span className="text-slate-600 dark:text-slate-400 text-sm font-medium">
                            {t.clientNotifications.daysAfterDue}
                          </span>
                        </div>
                      </div>
                      <div className="space-y-4">
                        <label className="text-sm font-bold text-slate-700 dark:text-slate-300 block">
                          {t.clientNotifications.emailTemplateLabel}
                        </label>
                        <div className="p-4 rounded-2xl border-2 border-purple-600 bg-white dark:bg-slate-900 shadow-sm transition-all">
                          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                            <div className="flex items-center gap-3 flex-1">
                              <div className="w-5 h-5 rounded-full border-2 border-purple-600 flex items-center justify-center shrink-0">
                                <div className="w-2.5 h-2.5 rounded-full bg-purple-600" />
                              </div>
                              <span className="text-sm font-bold text-slate-700 dark:text-slate-300">
                                {t.clientNotifications.standardTemplateChoice}
                              </span>
                            </div>
                            <select
                              value={templateLang}
                              onChange={(e) => {
                                setTemplateLang(e.target.value);
                              }}
                              className="bg-slate-100 dark:bg-slate-800 text-xs font-bold px-2 py-1.5 rounded-lg border-0 outline-none w-full sm:w-44 shrink-0"
                            >
                              <option value="ru">Русский</option>
                              <option value="cs">Čeština</option>
                              <option value="en">English</option>
                            </select>
                          </div>
                        </div>
                        <p className="text-[10px] text-slate-500 dark:text-slate-400 font-medium px-1 leading-relaxed">
                          {t.clientNotifications.autoEditNotice}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="pt-8">
                <div className="sticky bottom-0 z-[30] pt-4 pb-2 bg-gradient-to-t from-white dark:from-slate-900 via-white/95 dark:via-slate-900/95 to-transparent backdrop-blur-sm -mx-4 px-4 sm:mx-0 sm:px-0">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    disabled={!selectedClientId || isSaving}
                    onClick={handleOpenAutoPreview}
                    className="w-full py-4 rounded-2xl bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-black text-base sm:text-lg shadow-lg shadow-purple-500/30 hover:shadow-xl hover:shadow-purple-500/40 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center gap-3"
                  >
                    {isSaving ? (
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ repeat: Infinity, duration: 1 }}
                      >
                        <Clock className="h-6 w-6" />
                      </motion.div>
                    ) : (
                      <Eye className="h-6 w-6" />
                    )}
                    {t.clientNotifications.saveSettings}
                  </motion.button>
                </div>
              </div>
            </div>
          </section>
          */}

          {/* Manage Section */}
          {/* 
          <section className="relative overflow-hidden bg-white dark:bg-slate-800/50 rounded-3xl border border-slate-200 dark:border-slate-700 p-4 sm:p-6 md:p-8 shadow-sm transition-colors">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-8 border-b border-slate-100 dark:border-slate-800 pb-4 gap-4">
              <h2 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-slate-100">
                {t.clientNotifications.manageAutoSection}
              </h2>
              <div className="text-xs sm:text-sm font-bold px-4 py-1.5 bg-slate-100 dark:bg-slate-900 text-slate-500 rounded-full w-fit">
                {autoReminders.length} {t.common.total}
              </div>
            </div>

            <div className="">
              {isLoadingAuto ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {[...Array(3)].map((_, i) => (
                    <div
                      key={i}
                      className="h-48 bg-slate-50 dark:bg-slate-900/50 rounded-[2rem] animate-pulse"
                    />
                  ))}
                </div>
              ) : autoReminders.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <AnimatePresence>
                    {autoReminders.map((reminder) => (
                      <motion.div
                        key={reminder.id}
                        layout
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.9 }}
                        className="group relative bg-white dark:bg-slate-900 rounded-[2rem] p-5 sm:p-7 border border-slate-100 dark:border-slate-800 hover:border-purple-300 dark:hover:border-purple-700 shadow-sm hover:shadow-2xl transition-all duration-300"
                      >
                        <div className="mb-5">
                          <div className="flex items-center justify-between mb-2 gap-4">
                            <div className="flex items-center gap-3 min-w-0">
                              <div
                                className={`w-10 h-10 sm:w-12 sm:h-12 rounded-2xl flex items-center justify-center text-base sm:text-lg font-black shadow-inner border transition-colors shrink-0 ${
                                  reminder.isActive
                                    ? "bg-purple-50 dark:bg-purple-900/20 text-purple-600 border-purple-100 dark:border-purple-900/30"
                                    : "bg-slate-50 dark:bg-slate-800 text-slate-400 border-slate-100 dark:border-slate-700"
                                }`}
                              >
                                {reminder.clientName.charAt(0).toUpperCase()}
                              </div>
                              <h3 className="text-sm font-black text-slate-900 dark:text-slate-100 truncate leading-tight">
                                {reminder.clientName}
                              </h3>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              <Toggle
                                checked={reminder.isActive}
                                onChange={() =>
                                  handleToggleAutoStatus(reminder.id)
                                }
                                variant="green"
                                activeIcon={
                                  <Power className="w-3 h-3 text-emerald-600" />
                                }
                                inactiveIcon={
                                  <PowerOff className="w-3 h-3 text-amber-600" />
                                }
                              />
                              <button
                                onClick={() =>
                                  handleDeleteAutoReminder(reminder.id)
                                }
                                className="p-2 rounded-xl bg-rose-50 text-rose-600 border border-rose-100 hover:bg-rose-100 transition-all shadow-sm shrink-0"
                                title={t.common.delete}
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                          <div className="pl-[52px] sm:pl-[60px] -mt-1">
                            <p className="text-[10px] font-bold text-slate-500 break-all leading-tight">
                              {reminder.clientEmail}
                            </p>
                          </div>
                        </div>

                        <div className="space-y-4 bg-slate-50 dark:bg-slate-800/50 rounded-2xl p-4 sm:p-5 border border-slate-100 dark:border-slate-800">
                          <div className="space-y-1">
                            <p className="text-[10px] text-slate-400 font-black uppercase tracking-widest leading-none">
                              {t.clientNotifications.autoIntervalShort}
                            </p>
                            <p className="font-black text-slate-900 dark:text-slate-100 text-sm">
                              {reminder.daysAfterDue} {t.repeatingInvoices.days}
                            </p>
                          </div>
                          <div className="space-y-1">
                            <p className="text-[10px] text-slate-400 font-black uppercase tracking-widest leading-none">
                              {t.clientNotifications.emailTemplateShort}
                            </p>
                            <p className="font-black text-slate-900 dark:text-slate-100 text-sm">
                              {reminder.templateType === "standard"
                                ? (languages as any)[
                                    reminder.templateLang as keyof typeof languages
                                  ]?.name || reminder.templateLang
                                : t.clientNotifications.customTemplateChoice}
                            </p>
                          </div>
                        </div>

                        <div className="mt-5 flex flex-col sm:flex-row sm:items-center justify-between border-t border-slate-50 dark:border-slate-800 pt-4 gap-3">
                          <div className="flex items-center gap-2 px-3 py-1 rounded-full border transition-colors bg-white dark:bg-slate-900 border-slate-100 dark:border-slate-800 shadow-sm w-fit">
                            <div
                              className={`w-2 h-2 rounded-full transition-all ${
                                reminder.isActive
                                  ? "bg-emerald-500 animate-pulse shadow-[0_0_8px_rgba(16,185,129,0.5)]"
                                  : "bg-amber-500 shadow-[0_0_8px_rgba(245,158,11,0.4)]"
                              }`}
                            />
                            <span
                              className={`text-[10px] font-black uppercase tracking-widest transition-colors ${
                                reminder.isActive
                                  ? "text-emerald-600 dark:text-emerald-400"
                                  : "text-amber-600 dark:text-amber-500"
                              }`}
                            >
                              {reminder.isActive
                                ? t.clientNotifications.activeStatus
                                : t.clientNotifications.inactiveStatus}
                            </span>
                          </div>
                          <div className="text-[10px] text-slate-400 font-bold uppercase tracking-tighter">
                            {t.clientNotifications.lastSent}:{" "}
                            <span className="text-slate-500 dark:text-slate-300">
                              {reminder.lastSentAt
                                ? new Date(
                                    reminder.lastSentAt
                                  ).toLocaleDateString(locale)
                                : t.clientNotifications.neverSent}
                            </span>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </div>
              ) : (
                <div className="text-center py-16 bg-slate-50 dark:bg-slate-900/30 rounded-[3rem] border-2 border-dashed border-slate-100 dark:border-slate-800 mx-auto">
                  <Bell className="w-12 h-12 text-slate-200 dark:text-slate-800 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-slate-400 dark:text-slate-600 px-4">
                    {t.clientNotifications.noAutoReminders}
                  </h3>
                </div>
              )}
            </div>
          </section>
          */}
        </div>
      </div>

      <ClientDialog
        isOpen={isClientDialogOpen}
        onClose={() => setIsClientDialogOpen(false)}
        clients={clients}
        onAddClient={addClient}
        onUpdateClient={updateClient}
        onDeleteClient={deleteClient}
      />

      <EmailConfirmationModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        invoice={confirmInvoice}
        initialSubject={confirmSubject}
        initialText={confirmText}
        onSend={confirmSendManual}
        t={t}
        locale={locale}
        type={confirmationType}
        isEmailConfigured={
          !!(
            appSettings.notificationEmailHandle &&
            appSettings.notificationEmailName
          )
        }
      />
    </div>
  );
}
